import { List, Map } from "immutable";
import moment from "moment";

export type DirectoryPerson = {
	_id: string;
	name: string;
};

/**
 * A resolved cell. `people` keeps ids alongside names so person/people columns can still
 * link through to employee details the way the legacy table did; everything else collapses
 * to a display string at build time.
 */
export type DirectoryCellValue = { kind: "text"; text: string } | { kind: "people"; people: DirectoryPerson[] };

export type DirectoryRow = {
	_id: string;
	employee: DirectoryPerson;
	values: Record<string, DirectoryCellValue>;
};

export type DirectoryColumn = {
	_id: string;
	name: string;
	type: string;
};

const text = (value: string): DirectoryCellValue => ({ kind: "text", text: value });

/**
 * Custom-field dates are "dumb dates" — the stored time component is meaningless, so they are
 * read in UTC. Parsing them in the viewer's zone shifts anyone west of UTC back a day.
 */
const formatDumbDate = (value: unknown): string => {
	if (value === null || value === undefined || value === "") return "";
	const parsed = moment.utc(value as moment.MomentInput);
	return parsed.isValid() ? parsed.format("MMMM D, YYYY") : String(value);
};

const formatDate = (value: unknown): string => {
	if (value === null || value === undefined || value === "") return "";
	const parsed = moment(value as moment.MomentInput);
	return parsed.isValid() ? parsed.format("MMMM D, YYYY") : String(value);
};

type BuildDirectoryRowsArgs = {
	directoryList: List<string>;
	userMap: Map<string, any>;
	departmentMap: Map<string, any>;
	rolesMap: Map<string, any>;
	columns: DirectoryColumn[];
};

/**
 * Flattens the Redux user search result into plain rows for the table. Mirrors the value
 * resolution the legacy fixed-data-table cell did, minus the presentational bits.
 */
export const buildDirectoryRows = ({
	directoryList,
	userMap,
	departmentMap,
	rolesMap,
	columns,
}: BuildDirectoryRowsArgs): DirectoryRow[] =>
	directoryList.toArray().map((userId) => {
		const name = userMap.getIn([userId, "name"], "") as string;
		const values: Record<string, DirectoryCellValue> = {};

		columns.forEach((column) => {
			const raw = userMap.getIn([userId, "dataFields", column._id, "value"], "");

			switch (column.type) {
				case "person": {
					const personId = (raw || "") as string;
					const personName = userMap.getIn([personId, "name"], "") as string;
					values[column._id] =
						personId && personName
							? { kind: "people", people: [{ _id: personId, name: personName }] }
							: text("");
					break;
				}

				case "people": {
					const personIds: string[] = raw ? (List(raw).toArray() as string[]) : [];
					values[column._id] = {
						kind: "people",
						people: personIds
							.map((personId) => ({
								_id: personId,
								name: userMap.getIn([personId, "name"], "") as string,
							}))
							.filter((person) => person._id && person.name),
					};
					break;
				}

				case "department":
					values[column._id] = text((departmentMap.getIn([raw, "name"], "") as string) || "");
					break;

				case "role":
					values[column._id] = text((rolesMap.getIn([raw, "name"], "") as string) || "");
					break;

				case "dumb-date":
					values[column._id] = text(formatDumbDate(raw));
					break;

				case "date":
					values[column._id] = text(formatDate(raw));
					break;

				case "list":
				case "multi-select":
					values[column._id] = text(raw ? List(raw).toArray().filter(Boolean).join(", ") : "");
					break;

				default:
					values[column._id] = text(raw === null || raw === undefined ? "" : String(raw));
					break;
			}
		});

		return { _id: userId, employee: { _id: userId, name }, values };
	});
